import { Button } from '@components/Button';
import { ArticleCard } from '@components/Card/';
import { getBlogPostsByTag } from '@data/blog';
import { featuredArticlesMapping } from '@util/featuredArticlesMapping/';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';
import CardGridWithMobileCarousel from '..';
import {
  CarouselCtaWrapper,
  CarouselHeaderWrapper,
  CarouselHeadline,
  CarouselHeadlineLeft,
  NoResultCard,
} from '../style';
import { StyledBlock } from './styles';

export interface BlogHomepageFeaturedArticlesProps {
  articles?: any;
  contentfulTagIds?: string;
  header: string;
  locale: string;
  urlPrepend: string;
  blogType?: string;
  isShop?: boolean;
  pageType?: string;
  shopname?: string;
}

const BlogHomepageFeaturedArticles: FC<BlogHomepageFeaturedArticlesProps> = ({
  header,
  blogType,
  articles,
  contentfulTagIds = 'americas',
  locale,
  urlPrepend,
  isShop,
  pageType,
}) => {
  const t = useTranslations();
  const [featuredCardsArr, setFeaturedCardsArr] = useState<ReactNode[]>([]);

  const fetchAndSetBlogArr = async () => {
    try {
      const blogPostsData = await getBlogPostsByTag(contentfulTagIds, 3, locale, false);
      const mappedBlogPosts = await featuredArticlesMapping(blogPostsData?.items, urlPrepend, t);
      setFeaturedCardsArr(renderCardElements(mappedBlogPosts));
    } catch (error) {
      console.log('BlogHomepageFeaturedArticles error', error);
    }
  };

  useEffect(() => {
    if (articles && articles.length > 0) {
      // if manually selected articles are passed in, use those
      const mappedBlogPosts = featuredArticlesMapping(articles, urlPrepend, t);
      setFeaturedCardsArr(renderCardElements(mappedBlogPosts));
    } else {
      // otherwise, fetch the newest 3 blog posts
      fetchAndSetBlogArr();
    }
  }, [articles, contentfulTagIds]);

  // function to map over cardArr and create array of ShopCard components

  const renderCardElements = (cardArr: any[]) =>
    cardArr.map((card: any) => {
      return (
        <ArticleCard
          key={card.key}
          img={card.img}
          alt_text={card.alt_text}
          headline={card.headline}
          date={card.date}
          bodyText={card.bodyText}
          slug={card.slug}
          urlPrepend={card.urlPrepend}
          tags={card.tags}
          backgroundColor={false}
          locale={locale}
        />
      );
    });

  const url = isShop
    ? '/blog'
    : blogType === 'Trade'
    ? '/blog?type=real-estate-professionals'
    : '/blog?type=buyers-and-sellers';

  return (
    <StyledBlock $divider={pageType === 'blogHomepage'}>
      <CarouselHeaderWrapper>
        {pageType === 'blogHomepage' ? (
          <CarouselHeadlineLeft data-testid="featured-articles-headline-left">
            {header}
          </CarouselHeadlineLeft>
        ) : (
          <CarouselHeadline data-testid="featured-articles-headline">{header}</CarouselHeadline>
        )}
      </CarouselHeaderWrapper>
      {featuredCardsArr && featuredCardsArr.length > 0 ? (
        <CardGridWithMobileCarousel
          dataTestId="featured-articles"
          cardArr={featuredCardsArr}
          cardType="article"
        />
      ) : (
        <NoResultCard data-testid="no-featured-articles">{t('No Articles Found')}</NoResultCard>
      )}

      {pageType !== 'blogHomepage' && (
        <CarouselCtaWrapper>
          <Button
            data-testid="featured-articles-cta"
            url={url}
            label={t('View All Articles')}
            variant="secondary"
            arrow={false}
          />
        </CarouselCtaWrapper>
      )}
    </StyledBlock>
  );
};

export default BlogHomepageFeaturedArticles;
